import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  Icon,
  Icons,
  IconsProps,
} from '../../../../components';
import { AppRoutes } from '../../../../constants';
import { useGetUserPermissions, useSimActions } from '../../../../hooks';
import { DropdownPlacement, ProspectCategory, ProspectStatus, TextColor } from '../../../../types';
import { conditionalArray } from '../../../../utils';
import { SimActionType } from '../../SimActionModals';
import ExplorerItemEndIcon from '../ExplorerItemEndIcon';

interface ExplorerSimulationActionsProps {
  isDraft: boolean;
  isFavorited: boolean;
  isUsed: boolean;
  personaId: string;
  prospectCategory: ProspectCategory;
  prospectUserId: string;
  status: ProspectStatus;
  onClose: () => void;
  openSimModal: (type: SimActionType) => void;
}

const ExplorerSimulationActions = ({
  isDraft,
  isFavorited,
  isUsed,
  personaId,
  prospectCategory,
  prospectUserId,
  status,
  onClose,
  openSimModal,
}: ExplorerSimulationActionsProps) => {
  const navigate = useNavigate();

  const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);

  const { canUserEditLibrary, getCanUserEditSimInSelfService } = useGetUserPermissions();
  const canUserEditSimInSelfService = getCanUserEditSimInSelfService(prospectCategory, isDraft, prospectUserId);

  const { runReactivate, runFavorite, runUnfavorite } = useSimActions();

  const onActionClick = useCallback(
    (type: SimActionType) => {
      openSimModal(type);
      onClose();
    },
    [openSimModal, onClose]
  );

  const onEdit = useCallback(() => {
    navigate(`${AppRoutes.PROSPECT}/${personaId}`);
  }, [personaId, navigate]);

  const onUnarchive = useCallback(() => {
    runReactivate({
      id: personaId,
      onSuccess: onClose,
    });
  }, [personaId, runReactivate, onClose]);

  const onUnfavorite = useCallback(() => {
    runUnfavorite({
      id: personaId,
      onSuccess: onClose,
    });
  }, [personaId, runUnfavorite, onClose]);

  const onFavorite = useCallback(() => {
    runFavorite({
      id: personaId,
      onSuccess: onClose,
    });
  }, [personaId, runFavorite, onClose]);

  const actions: ({ label: string } & IconsProps)[] = useMemo(
    () => [
      ...conditionalArray(canUserEditSimInSelfService, {
        icon: Icon.EDIT,
        label: 'Edit',
        onClick: onEdit,
      }),
      ...conditionalArray(canUserEditLibrary, {
        icon: Icon.FOLDER_MOVE,
        label: 'Move',
        onClick: () => onActionClick(SimActionType.MOVE),
      }),
      // Archived simulations are only accessible to managers/admins,
      // so no need to check user permissions here.
      ...conditionalArray(status === ProspectStatus.ARCHIVED, {
        icon: Icon.ARCHIVE,
        label: 'Unarchive',
        onClick: onUnarchive,
      }),
      ...conditionalArray(canUserEditLibrary && status === ProspectStatus.ACTIVE, {
        icon: isUsed ? Icon.ARCHIVE : Icon.TRASH,
        label: isUsed ? 'Archive ' : 'Delete',
        color: TextColor.DESTRUCTIVE,
        onClick: () => onActionClick(isUsed ? SimActionType.ARCHIVE : SimActionType.DELETE),
      }),
      ...conditionalArray(isFavorited, {
        icon: Icon.STAR,
        color: 'stroke-orange-500',
        label: 'Remove from favorites',
        onClick: onUnfavorite,
      }),
      ...conditionalArray(!isFavorited, {
        icon: Icon.STAR,
        color: 'stroke-orange-500',
        fillColor: 'fill-orange-500',
        label: 'Add to favorites',
        onClick: onFavorite,
      }),
    ],
    [
      canUserEditLibrary,
      canUserEditSimInSelfService,
      isUsed,
      isFavorited,
      status,
      onActionClick,
      onEdit,
      onUnarchive,
      onUnfavorite,
      onFavorite,
    ]
  );

  return (
    <Dropdown
      open={isMoreDropdownOpen}
      onOpenChange={(open) => {
        setIsMoreDropdownOpen(open);
        onClose();
      }}
      placement={DropdownPlacement.BOTTOM_START}
    >
      <DropdownTrigger>
        <ExplorerItemEndIcon
          active={isMoreDropdownOpen}
          icon={Icon.ELLIPSIS_VERTICAL}
          onClick={() => setIsMoreDropdownOpen(!isMoreDropdownOpen)}
        />
      </DropdownTrigger>
      <DropdownContent>
        {actions.map((action) => (
          <DropdownItem
            key={action.label}
            label={action.label}
            icon={<Icons icon={action.icon} color={action.color} fillColor={action.fillColor} />}
            onClick={action.onClick}
          />
        ))}
      </DropdownContent>
    </Dropdown>
  );
};

export default ExplorerSimulationActions;
